import { css } from '@emotion/react';
import { BoundType } from '@flysas-tech/book-value-stream-models';
import { BREAKPOINTS, Divider, Radio, semanticTokens, tokens } from '@flysas-tech/commercial-ui';
import { PropsWithChildren, useLayoutEffect, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Flight, LowestFareOption } from 'shared';

import { FlightLowestFaresContainer, FlightLowestFareSkeleton } from '../FlightLowestFare';
import { FlightSegmentBar, FlightSegmentBarSkeleton } from '../FlightSegmentBar';
import { FlightTimesAndStops, FlightTimesAndStopsSkeleton } from '../FlightTimesAndStops';

import { LowestFares } from './FlightRowItemLowestFares';

const styles = {
  wrapper: css`
    box-shadow: ${tokens.shadow.medium};
    background-color: ${tokens.color.white};
    align-items: center;
    width: 100%;
    border-radius: none;
    cursor: pointer;

    &:has(.flight-row-focus:focus-within) {
      outline: ${semanticTokens.outline.focus.primary};
    }

    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      border-radius: ${tokens.dimension['space-4']};
    }
  `,
  row: (isSelected: boolean, isMultiProductMode: boolean) => css`
    display: flex;
    gap: ${tokens.dimension['space-3']};
    padding: ${tokens.dimension['space-5']};

    ${isMultiProductMode &&
    `
      flex-direction: column;
      justify-content: space-between;

      @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: ${tokens.dimension['space-6']};
        padding: ${tokens.dimension['space-6']};
        border-radius: ${tokens.dimension['space-4']};

        border-bottom: ${isSelected ? `1px solid ${tokens.color.gray['80']}` : 'none'};
        border-bottom-right-radius: ${isSelected ? `0px` : tokens.dimension['space-4']};
        border-bottom-left-radius: ${isSelected ? `0px` : tokens.dimension['space-4']};
        }
    `}

    ${!isMultiProductMode &&
    `
      flex-direction: row;
      align-items: center;
      gap: ${tokens.dimension['space-4']};

      @container main-container (min-width: ${BREAKPOINTS.layout.sm}px) {
        gap: ${tokens.dimension['space-6']};
      }
    `}

    ${!isMultiProductMode &&
    isSelected &&
    `
      outline: ${semanticTokens.outline.focus.secondary};
      @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
        border-radius: ${tokens.dimension['space-4']};
      }
    `}
  `,
  radioContainer: css`
    display: flex;
    align-items: center;
    justify-content: center;
    div:focus-within {
      border-color: transparent !important;
    }
  `,
  flightSegmentBar: (isMultiProductMode: boolean) => css`
    width: 100%;
    max-width: ${isMultiProductMode ? 'auto' : '400px'};
    display: ${isMultiProductMode ? 'block' : 'none'};
    @container main-container (min-width: ${BREAKPOINTS.layout.sm}px) {
      display: block;
    }
  `,
  dividerOnMobile: css`
    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      display: none;
    }
  `,
  dividerOnDesktop: (isMultiProductMode: boolean) => css`
    display: none;
    @container main-container (min-width: ${isMultiProductMode ? BREAKPOINTS.layout.md : BREAKPOINTS.layout.sm}px) {
      display: block;
    }
  `,
  dividerVertical: css`
    height: 48px;
    align-self: center;
  `,
  dividerHorizontal: css`
    margin-top: ${tokens.dimension['space-6']};
  `,
};

type Props = {
  pos: string;
  boundType: BoundType;
  flight: Flight;
  selectedProductId: string | undefined;
  handleRowClick: () => void;
  lowestFares?: LowestFareOption[];
  handleLowestFareClick?: (args: { productId: string; name: string } | undefined) => void;
};

export function FlightRowItem(props: PropsWithChildren<Props>) {
  const { pos, boundType, flight, selectedProductId, handleRowClick, lowestFares, handleLowestFareClick, children } =
    props;

  const scrollRef = useRef<HTMLLIElement>(null);

  const isSelected = flight.products.some(product => product.id === selectedProductId);
  const isDirect = flight.segments.length === 1;

  const isMultiProductMode = !!(handleLowestFareClick !== undefined && lowestFares && lowestFares.length > 0);

  useLayoutEffect(() => {
    if (isSelected) {
      setTimeout(() => {
        if (scrollRef.current) {
          const elementTop = scrollRef.current.getBoundingClientRect().top + window.scrollY;
          const offset = -80;
          window.scrollTo({ top: elementTop + offset, behavior: 'smooth' });
        }
      }, 150);
    }
  }, [isSelected]);

  return (
    <li key={flight.id} css={styles.wrapper} ref={scrollRef}>
      <div
        css={styles.row(isSelected, isMultiProductMode)}
        data-testid={`upsell-flight-row-${boundType}-${isDirect ? 'direct' : 'connecting'}`}
        onClick={handleRowClick}
        aria-expanded={isSelected}
      >
        {!isMultiProductMode && (
          <>
            <div css={styles.radioContainer}>
              <Radio
                className="flight-row-focus"
                label={''}
                size="small"
                aria-selected={isSelected}
                name={`flight-${boundType}`}
                checked={isSelected}
                onChange={handleRowClick}
              />
            </div>

            <div css={styles.dividerVertical}>
              <Divider variant="neutral" direction="vertical" />
            </div>
          </>
        )}

        <FlightTimesAndStops flight={flight} isResponsive={isMultiProductMode} />

        <div css={[styles.dividerVertical, styles.dividerOnDesktop(isMultiProductMode)]}>
          <Divider variant="neutral" direction="vertical" />
        </div>

        <div css={styles.flightSegmentBar(isMultiProductMode)}>
          <FlightSegmentBar flight={flight} />
        </div>

        {isMultiProductMode && (
          <>
            <div css={[styles.dividerVertical, styles.dividerOnDesktop(true)]}>
              <Divider variant="neutral" direction="vertical" />
            </div>
            <div css={[styles.dividerOnMobile, styles.dividerHorizontal]}>
              <Divider variant="neutral" direction="horizontal" />
            </div>

            <LowestFares
              lowestFares={lowestFares}
              pos={pos}
              selectedProductId={selectedProductId}
              handleLowestFareClick={handleLowestFareClick}
            />
          </>
        )}
      </div>
      {isSelected && isMultiProductMode && <>{children}</>}
    </li>
  );
}

type FlightRowItemSkeletonProps = {
  lowestFareItems?: number;
};

export function FlightRowItemSkeleton({ lowestFareItems = 0 }: FlightRowItemSkeletonProps) {
  const isMultiProductMode = lowestFareItems > 0;
  const noop = () => {};

  return (
    <li css={styles.wrapper}>
      <div css={styles.row(false, isMultiProductMode)} onClick={noop}>
        {!isMultiProductMode && (
          <>
            <div css={styles.radioContainer}>
              <Skeleton circle width={24} height={24} />
            </div>

            <div css={styles.dividerVertical}>
              <Divider variant="neutral" direction="vertical" />
            </div>
          </>
        )}

        <FlightTimesAndStopsSkeleton isResponsive={isMultiProductMode} />

        <div css={[styles.dividerVertical, styles.dividerOnDesktop(isMultiProductMode)]}>
          <Divider variant="neutral" direction="vertical" />
        </div>

        <div css={styles.flightSegmentBar(isMultiProductMode)}>
          <FlightSegmentBarSkeleton />
        </div>

        {isMultiProductMode && (
          <>
            <div css={[styles.dividerVertical, styles.dividerOnDesktop(true)]}>
              <Divider variant="neutral" direction="vertical" />
            </div>
            <div css={[styles.dividerOnMobile, styles.dividerHorizontal]}>
              <Divider variant="neutral" direction="horizontal" />
            </div>

            <FlightLowestFaresContainer>
              {Array.from({ length: lowestFareItems }, (_, index) => (
                <FlightLowestFareSkeleton key={index} />
              ))}
            </FlightLowestFaresContainer>
          </>
        )}
      </div>
    </li>
  );
}
